import dayjs from 'dayjs';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { calculateAverageUserRating } from './api/commonUtils';
import { refreshMovie, saveRating, setWatchlist } from './api/movies';
import { useSWRCacheManager } from './api/swrCacheManager';
import {
  RatingActionButton,
  RefreshActionButton,
  WatchlistActionButton,
} from './components/ActionButtons';
import { FadeInImage } from './components/FadeInImage';
import { ReleaseDateBadge } from './components/ReleaseDateBadge';
import {
  getRating,
  getRatingTier,
  getWatchlistDate,
  isWatchlisted,
  setRatingInfo,
  setWatchlistDate,
} from './helpers';
import { useLatestActivity, useRatingsOrWatchlist } from './hooks/useMovieData';
import { useMovieModal } from './MovieModalContext';
import { SetRatingPopup } from './SetRatingPopup';
import { UserContext } from './UserContext';

/**
 * @typedef {Object} MovieProps
 * @property {any} movie
 * @property {React.CSSProperties} [style]
 * @property {boolean} [onlyPoster]
 * @property {'regular' | 'compact'} [size]
 */

/** @type {React.ForwardRefExoticComponent<MovieProps & React.RefAttributes<HTMLDivElement>>} */
export const Movie = forwardRef(function Movie(props, ref) {
  const { movie, style = null, onlyPoster = false, size = 'regular' } = props;
  const userData = useContext(UserContext);
  const { openMovie, onMovieUpdate } = useMovieModal();
  const anchorRef = useRef();

  const [anchorEl, setAnchorEl] = useState(null);
  const [movieState, setMovieState] = useState(movie);

  // Keep local state in sync with prop (simpler than listener pattern)
  useEffect(() => {
    setMovieState(movie);
  }, [movie]);

  useEffect(() => {
    const unsubscribe = onMovieUpdate((updatedMovie) => {
      if (updatedMovie?.id !== movie.id) return;
      setMovieState((prev) => ({ ...prev, ...updatedMovie }));
    });

    return unsubscribe;
  }, [onMovieUpdate, movie.id]);

  const handlePopoverOpen = (event) => {
    setAnchorEl(anchorRef.current);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const { mutate: mutateActivity } = useLatestActivity();
  const { mutate: mutateUserRatings } = useRatingsOrWatchlist(
    userData.username,
    true,
  );
  const { mutate: mutateUserWatchlist } = useRatingsOrWatchlist(
    userData.username,
    false,
  );
  const { invalidateMovieCaches } = useSWRCacheManager();

  function toggleWatchlist() {
    const current_date = getWatchlistDate(movieState, userData.id);
    const watchlisted = isWatchlisted(movieState, userData.id);
    var info = {
      watchlist_date: current_date ? current_date : dayjs(new Date()),
      watchlisted: !watchlisted,
      movie_id: movieState.id,
      user_id: userData.id,
    };
    setWatchlist(info).then(() => {
      setMovieState({
        ...movieState,
        user_ratings: setWatchlistDate(movieState, info, userData),
      });
      // Invalidate all movie-related caches
      invalidateMovieCaches(movieState.id);
      mutateActivity();
      mutateUserWatchlist();
    });
  }

  function handleRatingSave(info) {
    saveRating(info).then(() => {
      const ratings = setRatingInfo(movieState, info, userData);
      setMovieState({
        ...movieState,
        user_ratings: ratings,
        user_average_rating: calculateAverageUserRating(ratings),
      });
      // Invalidate all movie-related caches
      invalidateMovieCaches(movieState.id);
      mutateActivity();
      mutateUserRatings();
    });
  }

  async function refresh() {
    const movie = await refreshMovie(movieState.id);
    setMovieState(movie);
    // Invalidate all movie-related caches
    invalidateMovieCaches(movieState.id);
  }

  function formatRuntime(runtime) {
    if (!runtime || Number.isNaN(Number(runtime))) return 'N/A';
    const totalMinutes = Number(runtime);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (!hours) return `${minutes}m`;
    return `${hours}h${minutes}m`;
  }

  const userRating = getRating(movieState, userData.id);
  const externalRating = movieState.average_rating
    ? Math.round(movieState.average_rating)
    : null;
  const userAverageRating = movieState.user_average_rating;

  return (
    <div className={`movie-card movie-card--${size}`} style={style} ref={ref}>
      {/* Ratings Section - Top badges */}
      {!onlyPoster && (
        <>
          {userAverageRating && (
            <div
              className={`activity-card__rating-badge activity-card__rating-badge--${getRatingTier(userAverageRating * 20)} rating-badge--left`}
              title={`User Average: ${userAverageRating.toFixed(1)}/5`}
            >
              {userAverageRating.toFixed(1)}
            </div>
          )}
          {externalRating && (
            <div
              className={`activity-card__rating-badge activity-card__rating-badge--${getRatingTier(externalRating)} rating-badge--right`}
              title={`Average Rating: ${externalRating}/100`}
            >
              {externalRating}
            </div>
          )}
        </>
      )}

      {/* Movie Info Section - Poster and Title */}
      <div
        className="movie-card__poster"
        onClick={() => openMovie(movieState.id)}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${movieState.title}`}
      >
        <FadeInImage
          loading="lazy"
          alt={movieState.title}
          src={
            movieState.poster_path
              ? `https://image.tmdb.org/t/p/w500${movieState.poster_path}`
              : null
          }
          fallbackSrc="/img/noPoster.webp"
        />
      </div>

      {!onlyPoster && (
        <>
          <div className="movie-card__info">
            <div
              className="movie-card__title"
              title={movieState.title}
              onClick={() => openMovie(movieState.id)}
              role="button"
              tabIndex={0}
            >
              {movieState.title}
            </div>
            <div className="movie-card__meta">
              <ReleaseDateBadge
                releaseDate={movieState.release_date}
                releaseYear={movieState.release_year}
                className="movie-card__year"
              />
              <span className="movie-card__separator">•</span>
              <span className="movie-card__runtime">
                {formatRuntime(movieState.runtime)}
              </span>
            </div>
          </div>

          {/* Actions Section - Bottom interaction buttons */}
          <div className="movie-card__actions" ref={anchorRef}>
            <div className="movie-card__action-group">
              <RatingActionButton
                userRating={userRating}
                onClick={handlePopoverOpen}
              />
              <SetRatingPopup
                movie={movieState}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                onRatingChanged={handleRatingSave}
              />
              <WatchlistActionButton
                isWatchlisted={isWatchlisted(movieState, userData.id)}
                onClick={toggleWatchlist}
              />
              <RefreshActionButton onClick={refresh} />
            </div>
          </div>
        </>
      )}
    </div>
  );
});
